import { Component, OnInit, Inject, signal } from '@angular/core';
import {
  MatDialog,
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose,
} from '@angular/material/dialog';
import { AutomaticallyPlannedRide, DaypartException, Ride, UserInRide } from 'src/app/interfaces';
import { getAuth } from 'firebase/auth';
import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDocs,
  query,
  setDoc,
  where,
} from 'firebase/firestore';
import { dataStore, db } from 'src/app/app.component';
import {
  getCopiedLocationsOrder,
  getExceptionsForDay,
  getRideTitle,
  getRideCopyVehicleError,
  getWeekString,
} from '../../helpers/ride-helper';
import moment from 'moment';
import { valueToDate } from 'src/app/globals';
import { changeReminderInQueue, removeFromReminderQueue } from '../../helpers/notification-helper';
import { PlanType } from 'src/app/enums';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatButton } from '@angular/material/button';
import { WarningDialog } from 'src/app/global-dialogs/warning/warning.component';

export interface DialogData {
  oldRide: Ride | AutomaticallyPlannedRide;
  newRide: Ride | AutomaticallyPlannedRide;
  delete?: boolean;
  deleteReason?: string;
  planType?: PlanType;
}
@Component({
  selector: 'app-copy-update',
  templateUrl: './copy-update.component.html',
  styleUrls: ['./copy-update.component.scss'],
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    MatDialogActions,
    MatButton,
    MatDialogClose,
  ],
})
export class CopyUpdateComponent implements OnInit {
  readonly planType = PlanType;
  userId: string;
  userEmail: string;
  organisationId = dataStore.selectedOrganisation.id as string;
  deleting = signal(false);
  exceptions = signal<DaypartException[]>([]);
  collectionName: string = this.data?.planType === PlanType.Automatic ? 'automaticRides' : 'rides';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<CopyUpdateComponent>,
  ) {}

  async ngOnInit() {
    console.log('data', this.data);
    this.userId = getAuth().currentUser.uid;
    this.userEmail = getAuth().currentUser.email;

    const exceptionsSnapshot = await getDocs(
      collection(db, `organisations/${this.organisationId}/dayPartExceptions`),
    );
    exceptionsSnapshot.forEach((exceptionDoc) => {
      const data = exceptionDoc.data() as DaypartException;
      data.id = exceptionDoc.id;
      this.exceptions.update((exceptions) => [...exceptions, data]);
    });
  }
  async updateCopies() {
    let promises = [];
    const oldRide = this.data.oldRide;
    const newRide = this.data.newRide;
    if (this.data.planType !== PlanType.Automatic) {
      const vehicleError = await getRideCopyVehicleError(this.organisationId, newRide);
      if (vehicleError) {
        this.dialog.open(WarningDialog, {
          data: { dialogType: 'rideCopyBlocked', optionalData: { exceptionName: vehicleError } },
        });
        return;
      }
    }
    const startOffset = this.calculateDateDifference(oldRide.start, newRide.start);
    const endOffset = this.calculateDateDifference(oldRide.end, newRide.end);

    const ridesToUpdate = [];
    const ridesSnapshot = await getDocs(
      query(
        collection(db, `organisations/${this.organisationId}/${this.collectionName}`),
        where('parentRideId', '==', oldRide.parentRideId),
        where('start', '>=', oldRide.start),
      ),
    );
    ridesSnapshot.forEach((rideDoc) => {
      const ride = rideDoc.data();
      if (rideDoc.id !== ride.parentRideId) {
        ride.id = rideDoc.id;
        ride.start = ride.start.toDate();
        ride.end = ride.end.toDate();
        ridesToUpdate.push(ride);
      }
    });

    for (const ride of ridesToUpdate) {
      const oldStartDate = ride.start;
      const newStartDate = new Date(oldStartDate);
      newStartDate.setMonth(newStartDate.getMonth() + startOffset.months);
      newStartDate.setDate(newStartDate.getDate() + startOffset.days);
      newStartDate.setHours(newRide.start.getHours());
      newStartDate.setMinutes(newRide.start.getMinutes());
      const oldEndDate = ride.end;
      const newEndDate = new Date(oldEndDate);
      newEndDate.setMonth(newEndDate.getMonth() + endOffset.months);
      newEndDate.setDate(newEndDate.getDate() + endOffset.days);
      newEndDate.setHours(newRide.end.getHours());
      newEndDate.setMinutes(newRide.end.getMinutes());
      if (this.data.planType === PlanType.Automatic) {
        const updatedRide: AutomaticallyPlannedRide = { ...newRide } as AutomaticallyPlannedRide;
        updatedRide.start = newStartDate;
        updatedRide.end = newEndDate;
        updatedRide.parentRideId = newRide.parentRideId;

        promises.push(
          setDoc(
            doc(db, `organisations/${this.organisationId}/${this.collectionName}/${ride.id}`),
            updatedRide,
            {
              merge: true,
            },
          ),
        );
      } else {
        const exception = getExceptionsForDay(newStartDate, newEndDate, this.exceptions());
        const updatedRide: Ride = { ...newRide };
        updatedRide.start = newStartDate;
        updatedRide.end = newEndDate;
        updatedRide.id = ride.id;
        updatedRide.year = newStartDate.getFullYear();
        updatedRide.month = newStartDate.getMonth();
        updatedRide.week = getWeekString(newStartDate);
        updatedRide.day = newStartDate.getDate();
        updatedRide.parentRideId = newRide.parentRideId;
        updatedRide.exception = exception
          ? {
              id: exception.id,
              title: exception.title,
              description: exception.description,
            }
          : deleteField();
        if (
          updatedRide.users &&
          (!updatedRide.users['_methodName'] || updatedRide.users['_methodName'] !== 'deleteField')
        ) {
          const updatedUsers = {};
          for (const [userId, userObj] of Object.entries(updatedRide.users) as [
            string,
            UserInRide,
          ][]) {
            // This is checked to make sure that deleted users are not included as a normal user. This has to be done this way as users is a map, not an array, so merge operations do not remove deleted users without deleteField().
            if (!userObj['_methodName'] || userObj['_methodName'] !== 'deleteField') {
              const pickupTime = moment(valueToDate(userObj.userPickupTime));
              const dropoffTime = moment(valueToDate(userObj.userDropoffTime));
              userObj.userPickupTime = moment(valueToDate(updatedRide.start))
                .set({ hours: pickupTime.hours(), minutes: pickupTime.minutes() })
                .toDate();
              userObj.userDropoffTime = moment(valueToDate(updatedRide.start))
                .set({ hours: dropoffTime.hours(), minutes: dropoffTime.minutes() })
                .toDate();
              updatedUsers[userId] = userObj;
            } else {
              updatedUsers[userId] = deleteField();
            }
          }
          updatedRide.users = updatedUsers as Map<string, any>;
          updatedRide.locationsOrder = getCopiedLocationsOrder(newRide as Ride, updatedRide);
        }
        updatedRide.title = getRideTitle(updatedRide);

        for (const key of Object.keys(updatedRide)) {
          if (
            updatedRide[key]['_methodName'] &&
            updatedRide[key]['_methodName'] === 'deleteField'
          ) {
            updatedRide[key] = deleteField();
          }
        }

        promises.push(
          setDoc(
            doc(db, `organisations/${this.organisationId}/${this.collectionName}/${ride.id}`),
            updatedRide,
            {
              merge: true,
            },
          ),
        );
        for (const user of Object.values(updatedRide.users) as UserInRide[]) {
          const oldUserData = ride.users ? (ride.users as Map<string, any>)[user.id] : null;
          if (!oldUserData) {
            continue;
          }
          const startChanged = !moment(valueToDate(oldUserData.userPickupTime)).isSame(
            moment(valueToDate(user.userPickupTime)),
          );
          const fromChanged = oldUserData.from !== user.from;
          const toChanged = oldUserData.to !== user.to;
          if (startChanged || fromChanged || toChanged) {
            promises.push(changeReminderInQueue(ride.id, user, this.organisationId));
          }
        }
      }
      if (promises.length >= 100) {
        await Promise.allSettled(promises);
        promises = [];
      }
    }
    if (promises.length > 0) {
      await Promise.allSettled(promises);
    }
    this.dialogRef.close('done');
  }

  async deleteCopies() {
    this.deleting.set(true);
    let promises = [];
    const oldRide = this.data.oldRide;
    const ridesToDelete = [];
    const ridesSnapshot = await getDocs(
      query(
        collection(db, `organisations/${this.organisationId}/${this.collectionName}`),
        where('parentRideId', '==', oldRide.parentRideId),
        where('start', '>', oldRide.start),
      ),
    );
    ridesSnapshot.forEach((rideDoc) => {
      const ride = rideDoc.data() as Ride;
      if (
        this.data?.planType === PlanType.Automatic &&
        ((ride as AutomaticallyPlannedRide).planningCompleted !== false ||
          (ride as AutomaticallyPlannedRide).rejected !== false)
      ) {
        return;
      }
      ride.id = rideDoc.id;
      ride.start = ride.start.toDate();
      ride.end = ride.end.toDate();
      ridesToDelete.push(ride);
    });

    for (const ride of ridesToDelete) {
      if (this.data?.planType !== PlanType.Automatic) {
        const backupRide = {} as any;
        backupRide.date = new Date();
        backupRide.doneByUser = this.userEmail;
        backupRide.doneByUid = this.userId;
        backupRide.deleteReason = this.data.deleteReason;
        backupRide.rideData = { ...ride };
        promises.push(
          setDoc(doc(db, `eventLogs/${this.organisationId}/deletedRides/${ride.id}`), backupRide),
        );
        const editDocs = await getDocs(
          collection(
            db,
            `organisations/${this.organisationId}/${this.collectionName}/${ride.id}/edits`,
          ),
        );
        editDocs.forEach((editDoc) => {
          promises.push(
            setDoc(
              doc(
                db,
                `eventLogs/${this.organisationId}/deletedRides/${ride.id}/edits/${editDoc.id}`,
              ),
              editDoc.data(),
            ),
          );
          promises.push(deleteDoc(editDoc.ref));
        });
      }
      promises.push(
        deleteDoc(
          doc(db, `organisations/${this.organisationId}/${this.collectionName}/${ride.id}`),
        ),
      );
      for (const user of Object.values(ride?.users ?? {}) as UserInRide[]) {
        promises.push(removeFromReminderQueue(ride.id, user, this.organisationId));
      }
      if (promises.length >= 100) {
        await Promise.allSettled(promises);
        promises = [];
      }
    }
    if (promises.length > 0) {
      await Promise.allSettled(promises);
    }
    this.deleting.set(false);
    this.dialogRef.close('done');
  }

  calculateDateDifference(oldDate: Date, newDate: Date) {
    const monthDiff = newDate.getMonth() - oldDate.getMonth();
    const dayDiff = newDate.getDate() - oldDate.getDate();
    return {
      months: monthDiff,
      days: dayDiff,
    };
  }
}
